import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Table, Button, Card, Space, Tag, Modal, Form, Input, 
    message, Popconfirm, Typography, Tabs, Tree, Divider
} from 'antd';
import { 
    PlusOutlined, EditOutlined, DeleteOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

// 辨识标签映射：平台=红，企业=蓝
const IDENTIFY_TAG_MAP = {
  platform: { label: '平台', color: 'red' },
  enterprise: { label: '企业', color: 'blue' },
};
const IDENTIFY_TAG_ORDER = ['platform', 'enterprise'];

// 在树形菜单节点行内展示辨识 tag（仅菜单类型节点，目录/按钮不展示）
const renderTitle = (node) => {
  const tags = node.identifyTags || [];
  return (
    <span>
      {node.title}
      {tags.length > 0 && (
        <span style={{ marginLeft: 6 }}>
          {IDENTIFY_TAG_ORDER.filter((v) => tags.includes(v)).map((v) => {
            const conf = IDENTIFY_TAG_MAP[v];
            return <Tag key={v} color={conf.color} style={{ marginLeft: 4 }}>{conf.label}</Tag>;
          })}
        </span>
      )}
    </span>
  );
};

// 递归为树节点注入辨识 tag 渲染
const decorateTreeData = (nodes) => nodes.map((node) => ({
  ...node,
  title: renderTitle(node),
  children: node.children ? decorateTreeData(node.children) : undefined,
}));

// 模拟菜单数据（从 MenuConfig.js 简化而来，用于菜单选择器）
const MOCK_MENU_DATA = [
    {
      key: '1',
      title: '首页',
      identifyTags: ['platform', 'enterprise'],
      children: []
    },
    {
      key: '2',
      title: '物模型管理',
      children: [
        { 
          key: '2-1', title: 'IOT类目',
          children: [
            { key: '2-1-1', title: '新增类目' },
            { key: '2-1-2', title: '删除类目' }
          ]
        },
        { key: '2-2', title: '设备能力', identifyTags: ['platform'] }
      ]
    },
    {
      key: '3',
      title: '产品管理',
      children: [
        { key: '3-1', title: '产品开发', identifyTags: ['enterprise'] },
        { 
          key: '3-2', title: '产品审核', identifyTags: ['platform'],
          children: [
            { key: '3-2-1', title: '通过审核' },
            { key: '3-2-2', title: '驳回审核' }
          ]
        }
      ]
    },
    {
      key: '4',
      title: '权限管理',
      children: [
        { key: '4-1', title: '菜单配置', identifyTags: ['platform'] },
        { key: '4-2', title: '内部角色', identifyTags: ['platform'] }
      ]
    }
];

// 展开所有父节点
const getExpandedKeys = (menuData) => {
    const expanded = [];
    const traverse = (nodes) => {
        nodes.forEach(node => {
            if (node.children && node.children.length > 0) {
                expanded.push(node.key);
                traverse(node.children);
            }
        });
    };
    traverse(menuData);
    return expanded;
};


const MenuGroup = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('ent1');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [editingGroup, setEditingGroup] = useState(null);
    const [checkedKeys, setCheckedKeys] = useState([]);
    const [expandedKeys, setExpandedKeys] = useState(getExpandedKeys(MOCK_MENU_DATA));

    // 模拟菜单分组数据
    const [groups, setGroups] = useState({
        ent1: [
            { id: 'e1_group_1', name: '应用服务商', remark: '应用服务商使用的菜单权限', menuKeys: ['1', '3-1', '2-1', '4-1'], includesCount: 4, associatedEnterprises: 25 },
            { id: 'e1_group_2', name: '设备制造商', remark: '设备制造商使用的菜单权限', menuKeys: ['1', '2-2', '3-2-1'], includesCount: 3, associatedEnterprises: 12 },
            { id: 'e1_group_3', name: '产品方案商', remark: '产品方案商使用的菜单权限', menuKeys: ['1', '2-1-1', '3-1', '4-2'], includesCount: 4, associatedEnterprises: 0 }
        ],
        ent2: [
            { id: 'e2_group_1', name: '默认子企业权限', remark: '二级企业门店管理人员权限', menuKeys: ['1', '3-1'], includesCount: 2, associatedEnterprises: 8 }
        ],
        ent3: [
            { id: 'e3_group_1', name: '默认三级企业权限', remark: '三级分销/加盟商管理权限', menuKeys: ['1'], includesCount: 1, associatedEnterprises: 3 }
        ]
    });

    const onCheck = (checkedKeysValue) => {
        setCheckedKeys(checkedKeysValue);
    };

    const onExpand = (expandedKeysValue) => {
        setExpandedKeys(expandedKeysValue);
    };

    const showModal = (group = null) => {
        setEditingGroup(group);
        if (group) {
            form.setFieldsValue(group);
            setCheckedKeys(group.menuKeys);
        } else {
            form.resetFields();
            setCheckedKeys([]);
        }
        setIsModalVisible(true);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            const newGroup = { 
                ...values, 
                menuKeys: checkedKeys,
                includesCount: checkedKeys.length,
                associatedEnterprises: editingGroup ? editingGroup.associatedEnterprises : 0
            };

            setGroups(prevGroups => {
                const updatedTabGroups = [...prevGroups[activeTab]];
                if (editingGroup) {
                    const index = updatedTabGroups.findIndex(g => g.id === editingGroup.id);
                    if (index > -1) {
                        updatedTabGroups[index] = { ...editingGroup, ...newGroup };
                    }
                    message.success('企业角色已更新');
                } else {
                    newGroup.id = `${activeTab}_role_${Date.now()}`;
                    updatedTabGroups.push(newGroup);
                    message.success('企业角色已新增');
                }
                return { ...prevGroups, [activeTab]: updatedTabGroups };
            });
            setIsModalVisible(false);
        } catch (error) {
            console.log('Validate Failed:', error);
        }
    };

    const handleDelete = (id) => {
        setGroups(prevGroups => {
            const updatedTabGroups = prevGroups[activeTab].filter(g => g.id !== id);
            message.success('企业角色已删除');
            return { ...prevGroups, [activeTab]: updatedTabGroups };
        });
    };

    const columns = [
        { title: '角色名称', dataIndex: 'name', key: 'name' },
        { 
            title: '包含菜单数', 
            dataIndex: 'includesCount', 
            key: 'includesCount',
            render: (count) => <Tag color="blue">{count}</Tag>
        },
        { title: '备注', dataIndex: 'remark', key: 'remark' },
        { 
            title: '关联企业', 
            dataIndex: 'associatedEnterprises', 
            key: 'associatedEnterprises',
            width: 100,
            align: 'center',
            render: (count, record) => {
                if (activeTab === 'ent2' || activeTab === 'ent3') {
                    return <Text>{count}</Text>;
                }
                return count > 0 ? (
                    <Button 
                        type="link" 
                        size="small" 
                        onClick={() => navigate(`/companyAdmin?role=${record.name}`)}
                    >
                        {count}
                    </Button>
                ) : (
                    <Text type="secondary">{count}</Text>
                );
            }
        },
        { 
            title: '操作', 
            key: 'action', 
            width: 180,
            render: (_, record) => (
                <Space split={<Divider type="vertical" />}>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => showModal(record)}>编辑</Button>
                    <Popconfirm
                        title="确定删除该角色吗？"
                        onConfirm={() => handleDelete(record.id)}
                        okText="确定"
                        cancelText="取消"
                        disabled={record.associatedEnterprises > 0}
                        okButtonProps={{ danger: true }}
                    >
                        <Button 
                            type="link" 
                            size="small" 
                            danger 
                            icon={<DeleteOutlined />} 
                            disabled={record.associatedEnterprises > 0}
                        >
                            删除
                        </Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ marginBottom: 16 }}>
                <Title level={4}>企业角色</Title>
            </div>
            <Tabs 
                activeKey={activeTab} 
                onChange={setActiveTab} 
                type="card"
                items={[
                    { 
                        key: 'ent1', 
                        label: '一级企业', 
                        children: (
                            <Card 
                                bordered={false} 
                                extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>一级企业角色</Button>}
                                bodyStyle={{ padding: '0 24px 24px 24px' }}
                            >
                                <Table
                                    columns={columns}
                                    dataSource={groups.ent1}
                                    rowKey="id"
                                    pagination={false}
                                />
                            </Card>
                        )
                    },
                    { 
                        key: 'ent2', 
                        label: '二级企业', 
                        children: (
                            <Card 
                                bordered={false} 
                                extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>二级企业角色</Button>}
                                bodyStyle={{ padding: '0 24px 24px 24px' }}
                            >
                                <Table
                                    columns={columns}
                                    dataSource={groups.ent2}
                                    rowKey="id"
                                    pagination={false}
                                />
                            </Card>
                        )
                    },
                    { 
                        key: 'ent3', 
                        label: '三级企业', 
                        children: (
                            <Card 
                                bordered={false} 
                                extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>三级企业角色</Button>}
                                bodyStyle={{ padding: '0 24px 24px 24px' }}
                            >
                                <Table
                                    columns={columns}
                                    dataSource={groups.ent3}
                                    rowKey="id"
                                    pagination={false}
                                />
                            </Card>
                        )
                    }
                ]}
            />

            <Modal
                title={editingGroup ? '编辑企业角色' : '新增企业角色'}
                open={isModalVisible}
                onOk={handleSave}
                onCancel={() => setIsModalVisible(false)}
                okText="保存"
                cancelText="取消"
                width={600}
                destroyOnClose
            >
                <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                    <Form.Item 
                        name="name" 
                        label="角色名称" 
                        rules={[{ required: true, message: '请输入角色名称' }]}
                    >
                        <Input placeholder="例如：运营组，财务组" />
                    </Form.Item>
                    <Form.Item 
                        name="remark" 
                        label="备注信息"
                    >
                        <Input.TextArea rows={3} placeholder="请输入角色的备注信息" />
                    </Form.Item>
                    <Form.Item 
                        name="menuSelection" 
                        label="角色菜单" 
                        rules={[{ required: true, message: '请选择至少一个菜单' }]}
                    >
                        <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, padding: '8px 4px', maxHeight: 300, overflowY: 'auto' }}>
                            <Tree
                                checkable
                                onExpand={onExpand}
                                expandedKeys={expandedKeys}
                                autoExpandParent={true}
                                onCheck={onCheck}
                                checkedKeys={checkedKeys}
                                treeData={decorateTreeData(MOCK_MENU_DATA)}
                            />
                        </div>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default MenuGroup;
